import { PanelLeftClose, Settings } from 'lucide-react';
import { shellCapabilities, useAuth } from '@/shared/auth';
import { cn } from '@/shared/lib/cn';
import { AccountMenu } from './AccountMenu';
import { settingsEntry, type NavSearch } from './adminNavigation';
import { CommandBar } from './CommandBar';
import { NavEntryLink, ShellLogo } from './NavEntryLink';
import {
  settingsVisible,
  visibleAreas,
  visiblePlatformEntries,
  type ActivePlace,
} from './navVisibility';
import { ObjectScopeMenu } from './ObjectScopeMenu';
import { SidebarAreaList } from './SidebarAreaList';
import { SidebarRail } from './SidebarRail';
import type { NavCounts } from './useNavCounts';
import { WorkspaceRow } from './WorkspaceRow';

interface ShellSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  place: ActivePlace;
  path: string;
  search: NavSearch;
  counts: NavCounts;
  onLogout: () => void;
}

/** Weiße Seitenleiste, 268 px ausgeklappt oder 80 px eingeklappt (Baukasten „Gerüst“). */
export function ShellSidebar(props: ShellSidebarProps) {
  const { collapsed, onToggle, place, path, search, counts, onLogout } = props;
  const { user } = useAuth();
  const capabilities = shellCapabilities(user);
  const areas = visibleAreas(user);
  const platform = visiblePlatformEntries(user);
  const settingsActive = place?.kind === 'settings';

  if (collapsed) {
    return (
      <SidebarRail
        {...{ areas, platform, place, counts, onToggle, onLogout }}
        objectScope={capabilities.objectScope}
        search={capabilities.commandBar}
        settings={settingsVisible(user)}
      />
    );
  }

  return (
    <aside
      id="admin-navigation"
      aria-label="Hauptnavigation"
      className="border-hairline flex w-[268px] flex-none flex-col gap-3 border-r bg-white px-3.5 py-[18px]"
    >
      <div className="flex items-center gap-2.5 px-1">
        <ShellLogo />
        <span className="flex-1 font-serif text-[22px] font-extrabold tracking-[-0.02em]">
          StayOn
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Leiste einklappen"
          className="border-hairline text-ink rounded-square flex size-10 items-center justify-center border bg-white"
        >
          <PanelLeftClose size={18} aria-hidden />
        </button>
      </div>
      <WorkspaceRow />
      {capabilities.objectScope && <ObjectScopeMenu />}
      {capabilities.commandBar && <CommandBar variant="sidebar" />}
      <nav aria-label="Bereiche" className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
        <SidebarAreaList {...{ areas, platform, place, path, search, counts }} />
      </nav>
      {settingsVisible(user) && (
        <NavEntryLink
          entry={settingsEntry}
          active={settingsActive}
          className={cn(
            'rounded-control flex h-[42px] items-center gap-3 px-3 text-sm font-semibold',
            settingsActive ? 'bg-ink text-white' : 'text-ink hover:bg-card-alt',
          )}
        >
          <Settings size={19} aria-hidden />
          Einstellungen
        </NavEntryLink>
      )}
      <AccountMenu variant="sidebar" onLogout={onLogout} />
    </aside>
  );
}
